<script setup lang='ts'>
import { storeToRefs } from 'pinia';
import { computed } from 'vue';

import { useFiltersStore } from '@/stores/useFiltersStore';

const props = defineProps<{
  options: Array<Option>;
  selectedOption: Option;
}>();

const emits = defineEmits<{
  (e: 'select', value: Option): void;
}>();

const filtersStore = useFiltersStore();
const { filters } = storeToRefs(filtersStore);

type Option = {
  label: string;
  value: string;
};

const savedSelection = computed(() => filters.value.sorts.length ? props.options.find(it => it.value === filters.value.sorts[0].type)! : props.options[0]);
</script>

<template>
  <ul id="options-list" role="listbox" class="options-list">
    <div
      v-for="(option, index) in props.options"
      :key="index"
      class="option-row"
      tabindex="0"
    >
      <li
        role="option"
        :aria-selected="option.label === savedSelection.label"
        tabindex="0"
        class="option"
        @click="emits('select', option)"
        @keydown="emits('select', option)"
      >
        {{ option.label }}
      </li>
      <div>
        <input
          type="checkbox" :value="option" :checked="option.label === savedSelection.label"
          :aria-label="option.label" :aria-checked="option.label === savedSelection.label"
          @input="emits('select', option)"
        >
      </div>
    </div>
  </ul>
</template>

<style lang="css" scoped>
.options-list {
  list-style-type: none;
  padding-block: var(--yb-spacing-xs);
  padding-inline-start: 0;
}

.option-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--yb-spacing-sm);
  border-radius: var(--yb-radius-sm);
  outline: none;

  input[type='checkbox'] {
    appearance: none;
    -webkit-appearance: none;
    position: relative;
    width: 20px;
    aspect-ratio: 1;
    border: 1px solid var(--yb-color-mediumgray);
    border-radius: 2px;
    background-color: var(--yb-color-white);
    margin-inline: var(--yb-spacing-xs);
    accent-color: var(--yb-color-primary);
  }

  input[type='checkbox']:checked {
    background-color: var(--yb-color-primary);
    border-color: var(--yb-color-primary);
    color: var(--yb-color-white);
  }

  input[type='checkbox']:checked::before {
    content: '\2713';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: block;
    text-align: center;
    color: var(--yb-color-white);
    font-size: 16px;
  }
}

.option {
  width: 100%;
  padding-block: var(--yb-spacing-xs);
  color: var(--yb-color-black);
  text-wrap: nowrap;
  outline: none;
  cursor: pointer;
}
</style>
